<script setup>
// Builder.io
import { Content, isPreviewing, isEditing } from '@builder.io/sdk-vue';
// Internal
import { getComponents } from '@/composables/builder/blocks';
import { getBuilderContent } from '@/composables/builder/content';
import { getSeoOptions } from '@/composables/builder/seo';
import { getModelFromURL } from '@/composables/builder/model';
import { useGlobalStore } from '@/stores/global';

const { $config } = useNuxtApp();
const { urlExceptions } = useGlobalStore() || {};

const route = useRoute();

const model_options = getModelFromURL(route.path, 'page', $config, urlExceptions);
const model = model_options.model ? model_options.model : 'page';

const can_show_content = ref(isPreviewing(route.fullPath));
const builder_content = ref(isPreviewing(route.fullPath));

const { content, api_key, error } = await getBuilderContent();

if (error.statusCode === 500) {
    throw error;
}

if (!isPreviewing(route.fullPath)) {
    if (error) {
        throw error;
    }
    if (content.value?.data && model !== 'nuxt') {
        builder_content.value = content.value;
        const seoOptions = getSeoOptions(content.value, model);
        if (seoOptions && seoOptions.title) {
            if (content.value.data?.seo?.seo?.removeTitleTail || seoOptions.title.includes('|')) {
                useHead({
                    titleTemplate: '%s',
                });
            }
            useSeoMeta({
                title: seoOptions.title,
                ogTitle: seoOptions.title,
                description: seoOptions.description,
                ogDescription: seoOptions.description,
                ogImage: seoOptions.webpage?.image || '/social.jpg',
                twitterCard: 'summary_large_image',
            });
        }
    } else {
        builder_content.value = false;
    }
    can_show_content.value = content ? true : isPreviewing(route.fullPath);
    builder_content.value = content ? content.value : isPreviewing(route.fullPath);
}

const editorUpdate = (event) => {
    const { data } = event.data;
    const new_content = { ...content.value };
    new_content.data = { ...new_content.data, ...data.data.data };
    content.value = new_content;
    can_show_content.value = true;
};

const builderMessage = (event) => {
    if (event.data?.type !== 'builder.contentUpdate') return;
    editorUpdate(event);
};

onMounted(() => {
    if (isPreviewing(route.fullPath)) {
        window.addEventListener('message', builderMessage, '*');
    }
});

onBeforeUnmount(() => {
    if (isPreviewing(route.fullPath)) {
        window.removeEventListener('message', builderMessage);
    }
});
</script>

<template>
    <ProjectPage :page="content" :data-model="model" v-if="can_show_content">
        <!-- Release -->
        <TemplatesBlogHero :page="content" v-if="model === 'blog'" />
        <Content
            :api-key="api_key"
            :model="model"
            :content="builder_content"
            :customComponents="getComponents(api_key)"
        />
        <template v-slot:footer v-if="model === 'blog'">
            <TemplatesBlogFooter :page="content" />
        </template>
    </ProjectPage>
</template>

<style lang="scss">

.page-wrapper {
    [class*='builder-pixel'] {
        position: absolute;
    }
}

.page-wrapper[data-model='page'] {
    background-color: var(--pink);
}

.page-wrapper[data-model='blog'] {
    
    background-color: var(--pink);
    color: var(--magenta);
    
    .breadcrumbs {
        z-index: 1;
    }

    .template-blog-hero {
        margin-bottom: 2.25rem;
    }
    .root-container {
        max-width: 1130px;
        width: calc(1130 / 1440 * 100vw);
        margin: auto;
        --block-padding: 2.25rem;

        .builder-blocks {
            margin-top: 0 !important;
        }

        .container {
            max-width: 100%;
            .inner-container {
                max-width: 100%;
            }
        }

        .builder-text {
            max-width: 960px;
            padding: 0;

            @media (min-width: $tablet) {
                padding: var(--block-padding) 30px;
            }
        }

       .builder-block > picture:first-child {
            img {
                border-radius: var(--radius);
            }
        }
    }

    @media (max-width: $tablet) {
        .root-container {
            width: 90vw;
        }
    }
}
</style>

<!-- Blog content typography — must be outside @layer legacy so it beats Builder's unlayered resets -->
<style>
[data-model='blog'] main .builder-text {
    color: var(--magenta, #46062b);
}

/* Body text */
[data-model='blog'] main .builder-text p,
[data-model='blog'] main .builder-text ol,
[data-model='blog'] main .builder-text ul {
    font-family: var(--font-hubspot-sans) !important;
    font-size: 16px;
    letter-spacing: 0.32px;
    line-height: 26px;
    font-weight: 300;
    margin-bottom: 18px;
}

/* Bold text in the blog body: Medium (500). The original ticket asked for 300,
   but the body copy is also 300, so 300 made bold indistinguishable from normal
   text (read as "bold not displaying"). 500 keeps emphasis while staying lighter
   than the old browser-default 700. */
[data-model='blog'] main .builder-text strong {
    font-weight: 500;
}

/* Headings — HubSpot Serif Book (!important overrides Builder inline styles) */
[data-model='blog'] main .builder-text h1,
[data-model='blog'] main .builder-text h2 {
    font-family: var(--font-hubspot-serif) !important;
    font-size: calc(60 / var(--base-fs) * 1rem) !important;
    line-height: 1.1;
    font-weight: 400;
    letter-spacing: 0;
    margin-bottom: 18px;
}

[data-model='blog'] main .builder-text h3 {
    font-family: var(--font-hubspot-serif) !important;
    font-size: calc(36 / var(--base-fs) * 1rem) !important;
    line-height: 1.2;
    font-weight: 400;
    letter-spacing: 0;
    margin-bottom: 18px;
}

[data-model='blog'] main .builder-text h4 {
    font-family: var(--font-hubspot-serif) !important;
    font-size: calc(30 / var(--base-fs) * 1rem) !important;
    line-height: 1.3;
    font-weight: 400;
    letter-spacing: 0;
    margin-bottom: 18px;
}

[data-model='blog'] main .builder-text h5 {
    font-family: var(--font-hubspot-serif) !important;
    font-size: calc(24 / var(--base-fs) * 1rem) !important;
    line-height: 1.3;
    font-weight: 400;
    letter-spacing: 0;
    margin-bottom: 18px;
}

[data-model='blog'] main .builder-text h6 {
    font-family: var(--font-hubspot-serif) !important;
    font-size: calc(20 / var(--base-fs) * 1rem) !important;
    line-height: 1.3;
    font-weight: 400;
    letter-spacing: 0;
    margin-bottom: 18px;
}

/* Lists — purple shard bullets */
[data-model='blog'] main .builder-text ul {
    list-style: none;
    padding-left: 0;
    margin-left: 0;
    display: flex;
    flex-direction: column;
    gap: 14px;
}

[data-model='blog'] main .builder-text ul li {
    font-family: var(--font-hubspot-sans) !important;
    font-size: 16px;
    line-height: 26px;
    letter-spacing: 0.32px;
    font-weight: 300;
    margin-bottom: 0;
    display: flex;
    align-items: baseline;
    gap: 10px;
}

[data-model='blog'] main .builder-text ul li::before {
    content: '';
    width: 5px;
    height: 6px;
    background: url('/img/2026/bullet-shard-purple.svg') no-repeat center / contain;
    flex-shrink: 0;
    margin-top: 10px;
}

[data-model='blog'] main .builder-text ol {
    padding-left: 0;
    margin-left: 0;
    list-style: none;
    counter-reset: blog-ol;
    display: flex;
    flex-direction: column;
    gap: 14px;
}

[data-model='blog'] main .builder-text ol li {
    font-family: var(--font-hubspot-sans);
    font-size: 16px;
    line-height: 26px;
    letter-spacing: 0.32px;
    font-weight: 300;
    margin-bottom: 0;
    counter-increment: blog-ol;
    list-style: none;
    display: flex;
    align-items: baseline;
    gap: 10px;
}

[data-model='blog'] main .builder-text ol li::before {
    content: counter(blog-ol) ".";
    font-family: var(--font-hubspot-sans);
    font-size: 16px;
    line-height: 26px;
    letter-spacing: 0.32px;
    font-weight: 300;
    color: var(--purple, #820CA7);
    margin-left: 0;
    flex-shrink: 0;
}

/* Links */
[data-model='blog'] main .builder-text a {
    color: var(--magenta, #46062b);
    text-decoration: underline;
    text-decoration-style: dotted;
}

[data-model='blog'] main .builder-text a:hover {
    text-decoration: none;
    color: var(--purple, #820CA7);
}
</style>
